import { useState } from "react";
import axios from "axios";
import { ArrowUpRight, Loader2, CheckCircle2, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import { PageHeader } from "@/components/site/PageHeader";
import { Reveal } from "@/components/site/motion";
import { Seo } from "@/components/site/Seo";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PRODUCT_TYPES = ["Solar Roof Ventilation", "Solar Hot Water"];

const Field = ({ label, children, full }) => (
  <label className={`block ${full ? "sm:col-span-2" : ""}`}>
    <span className="block text-xs tracking-[0.15em] uppercase text-zinc-500 mb-3 font-display">{label}</span>
    {children}
  </label>
);

const inputCls =
  "w-full bg-transparent border-b border-[#333] focus:border-[#fcc41b] text-white text-lg py-3 outline-none transition-colors duration-300 placeholder:text-zinc-600";

export default function WarrantyRegistration() {
  const [form, setForm] = useState({
    name: "", phone: "", email: "", street_address: "", installer_address: "",
    product_type: "", model_number: "", install_date: "", serial_numbers: "",
    company_website: "",
  });
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    const required = ["name", "phone", "email", "street_address", "installer_address", "product_type", "model_number", "install_date", "serial_numbers"];
    const missing = required.filter((k) => !form[k] || !String(form[k]).trim());
    if (missing.length) {
      toast.error("Please complete all required fields.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/warranty`, form);
      setDone(true);
      toast.success("Warranty registered — a confirmation is on its way.");
      setForm({
        name: "", phone: "", email: "", street_address: "", installer_address: "",
        product_type: "", model_number: "", install_date: "", serial_numbers: "", company_website: "",
      });
      window.scrollTo(0, 0);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : detail?.[0]?.msg || "Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Seo
        path="/warranty-registration"
        title="Warranty Registration"
        description="Register your SolarArk solar roof ventilation or solar hot water system warranty online. Fast, secure and keeps your product cover on record."
      />
      <PageHeader
        overline="Warranty"
        title="Warranty Registration."
        subtitle="Register your SolarArk solar roof ventilation or solar hot water system to keep your warranty on record. All fields are required."
      />

      <section data-testid="warranty-section" className="relative py-16 lg:py-24 bg-[#050505] border-t border-[#1c1c1c]">
        <div className="max-w-3xl mx-auto px-6 lg:px-12">
          {done ? (
            <Reveal>
              <div data-testid="warranty-success" className="rounded-3xl border border-[#fcc41b]/30 bg-[#0d0d0d] p-10 lg:p-14 flex flex-col items-start gap-6">
                <ShieldCheck size={56} className="text-[#fcc41b]" />
                <h2 className="font-display font-semibold text-3xl text-white">Warranty registered.</h2>
                <p className="text-zinc-400 text-lg leading-relaxed">
                  Thanks — your SolarArk warranty registration has been received. A confirmation email with a summary of your details is on its way. Please keep it for your records.
                </p>
                <button onClick={() => setDone(false)} className="text-[#fcc41b] font-display font-medium" data-testid="warranty-reset">
                  Register another system
                </button>
              </div>
            </Reveal>
          ) : (
            <Reveal>
              <form onSubmit={submit} noValidate data-testid="warranty-form" className="rounded-3xl border border-[#262626] bg-[#0d0d0d] p-8 lg:p-12 grid sm:grid-cols-2 gap-x-8 gap-y-8">
                <input
                  type="text"
                  name="company_website"
                  value={form.company_website}
                  onChange={set("company_website")}
                  tabIndex={-1}
                  autoComplete="off"
                  aria-hidden="true"
                  className="absolute -left-[9999px] top-auto h-px w-px opacity-0 pointer-events-none"
                />
                <Field label="Full Name *">
                  <input data-testid="warranty-name" className={inputCls} value={form.name} onChange={set("name")} placeholder="Jane Smith" required />
                </Field>
                <Field label="Phone Number *">
                  <input data-testid="warranty-phone" className={inputCls} value={form.phone} onChange={set("phone")} placeholder="0400 000 000" required />
                </Field>
                <Field label="Email Address *">
                  <input data-testid="warranty-email" type="email" className={inputCls} value={form.email} onChange={set("email")} placeholder="jane@email.com" required />
                </Field>
                <Field label="Product Type *">
                  <select data-testid="warranty-product-type" className={`${inputCls} cursor-pointer`} value={form.product_type} onChange={set("product_type")} required>
                    <option value="" className="bg-[#0d0d0d]">Select…</option>
                    {PRODUCT_TYPES.map((o) => <option key={o} value={o} className="bg-[#0d0d0d]">{o}</option>)}
                  </select>
                </Field>
                <Field label="Street Address *" full>
                  <input data-testid="warranty-street-address" className={inputCls} value={form.street_address} onChange={set("street_address")} placeholder="12 Example Street, Suburb, STATE 2000" required />
                </Field>
                <Field label="Installer Name / Address *" full>
                  <input data-testid="warranty-installer-address" className={inputCls} value={form.installer_address} onChange={set("installer_address")} placeholder="Installer business name and address (or 'DIY install')" required />
                </Field>
                <Field label="System Model Number *">
                  <input data-testid="warranty-model-number" className={inputCls} value={form.model_number} onChange={set("model_number")} placeholder="e.g. SAV-60T" required />
                </Field>
                <Field label="Date of Installation or Purchase *">
                  <input data-testid="warranty-install-date" type="date" className={`${inputCls} cursor-pointer [color-scheme:dark]`} value={form.install_date} onChange={set("install_date")} required />
                </Field>
                <Field label="System Serial Number(s) *" full>
                  <textarea data-testid="warranty-serial-numbers" rows={3} className={`${inputCls} resize-none`} value={form.serial_numbers} onChange={set("serial_numbers")} placeholder="Enter the serial number for each system, separated by commas or new lines" required />
                </Field>
                <div className="sm:col-span-2">
                  <button
                    type="submit"
                    data-testid="warranty-submit"
                    disabled={loading}
                    className="group w-full sm:w-auto inline-flex items-center justify-center gap-3 rounded-full bg-[#fcc41b] text-black font-display font-semibold px-8 py-4 hover:bg-[#e5ae13] active:scale-95 transition-[background-color,transform] duration-300 disabled:opacity-60"
                  >
                    {loading ? <><Loader2 size={20} className="animate-spin" /> Registering…</> : <>Register Warranty <ArrowUpRight size={20} className="group-hover:translate-x-1 group-hover:-translate-y-1 transition-transform duration-300" /></>}
                  </button>
                </div>
              </form>
            </Reveal>
          )}
        </div>
      </section>
    </>
  );
}
